// Top-up checkout flow types for the SaaS billing prototype.
// See plans/saas-billing/09-ai-credit-top-up.md.
//
// Defines the shapes returned by the mock checkout / completion methods on
// IBillingMockService. Real implementation replaces these with Stripe Checkout
// Session payloads + webhook-driven success state.

import { TopUpPack, TopUpPrice } from './top-up.model';

/**
 * Typed error union for the top-up checkout flow. Returned by both
 * initiateTopUpCheckout and simulateTopUpCompletion so callers can branch on
 * a deterministic code rather than parsing a string.
 *
 * - PACK_NOT_FOUND: requested packId does not exist in the catalog.
 * - PRICE_NOT_AVAILABLE_FOR_CURRENCY: the pack has no TopUpPrice entry for
 *   the requested currency/country pair.
 * - STRIPE_DECLINED: Stripe declined the charge (simulated via
 *   __simulateTopUpFailure in the prototype).
 * - CHECKOUT_SESSION_INVALID: completion called with a session id that was
 *   never issued (or has the wrong format).
 * - NETWORK_ERROR: reserved for future transient failures; not emitted by the
 *   current mock but typed so callers can render a generic retry state.
 */
export type TopUpCheckoutError =
  | 'PACK_NOT_FOUND'
  | 'PRICE_NOT_AVAILABLE_FOR_CURRENCY'
  | 'STRIPE_DECLINED'
  | 'CHECKOUT_SESSION_INVALID'
  | 'NETWORK_ERROR';

/**
 * Success result of a completed top-up checkout. Includes the pack that was
 * purchased, the matched TopUpPrice (currency + amountMinor), the tenant's
 * new top-up balance (post-increment), a placeholder receipt URL the UI
 * surfaces as "View receipt", and the original checkout session id for
 * audit/correlation.
 */
export interface TopUpSuccess {
  pack: TopUpPack;
  pricePoint: TopUpPrice;
  newTopUpBalance: number;
  receiptUrl: string;
  checkoutSessionId: string;
}
